/**
 * Send Invoice Modal
 * Email form with recipient, subject, message, and tracking options.
 */

import React, { useState } from 'react';
import { X, Send, AlertTriangle, Paperclip, Link, Bell } from 'lucide-react';
import {
  invoiceAPI,
  Invoice,
  formatCents,
} from '@/services/api/invoiceAPI';

interface SendInvoiceModalProps {
  organizationId: number;
  invoice: Invoice;
  onClose: () => void;
  onSent: () => void;
}

export function SendInvoiceModal({ organizationId, invoice, onClose, onSent }: SendInvoiceModalProps) {
  const defaultEmail = invoice.customer_snapshot?.email || '';
  const [recipientEmail, setRecipientEmail] = useState(defaultEmail);
  const [ccEmails, setCcEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [includePortalLink, setIncludePortalLink] = useState(true);
  const [scheduleReminders, setScheduleReminders] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    if (!recipientEmail) {
      setError('Recipient email is required');
      return;
    }

    setSending(true);
    setError(null);

    try {
      const ccList = ccEmails ? ccEmails.split(',').map(e => e.trim()).filter(Boolean) : undefined;

      await invoiceAPI.sendInvoiceEmail(organizationId, invoice.id, {
        recipient_email: recipientEmail,
        subject: subject || undefined,
        message: message || undefined,
        cc_emails: ccList,
        include_portal_link: includePortalLink,
        schedule_reminders: scheduleReminders,
      });

      onSent();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Send Invoice</h3>
            <p className="text-sm text-gray-500">{invoice.invoice_number} &middot; {formatCents(invoice.total_cents)}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To *</label>
            <input
              type="email"
              value={recipientEmail}
              onChange={e => setRecipientEmail(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="customer@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CC (comma-separated)</label>
            <input
              type="text"
              value={ccEmails}
              onChange={e => setCcEmails(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="cc1@example.com, cc2@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subject <span className="text-gray-400 font-normal">(auto-generated if blank)</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder={`Invoice ${invoice.invoice_number} from ${organizationId}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Message <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Add a custom message to your customer..."
            />
          </div>

          {/* Options */}
          <div className="space-y-2.5 pt-1">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={includePortalLink} onChange={e => setIncludePortalLink(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <div className="flex items-center gap-1.5">
                <Link className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Include payment portal link</span>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={scheduleReminders} onChange={e => setScheduleReminders(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <div className="flex items-center gap-1.5">
                <Bell className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Schedule automatic reminders</span>
              </div>
            </label>
          </div>

          {/* Attachment indicator */}
          <div className="flex items-center gap-2 p-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <Paperclip className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">Invoice PDF will be attached automatically</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !recipientEmail}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Sending...' : 'Send Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}
